import { SuggestionsType } from '@analytics/const';
import { AnyQueryAnalyticsService, SearchAnalyticsService } from '@analytics/feature';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { CustomFacetContentType } from '@catalog/const';
import { CustomSearchBoxConfig, CustomSearchResults, SearchHistoryItem, Sts, Tap } from '@search/const';
import {
  CustomSearchBoxService,
  SearchCurrentQueryService,
  SearchFacetService,
  SearchHistoryService,
} from '@search/feature';
import { CUSTOM_ICON_TYPE } from '@shared/models';
import { DeviceService, UrlNavigatorService } from '@shared/services';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';

const DEFAULT_CONFIG: CustomSearchBoxConfig = {
  minCharactersBeforeRequest: 2,
  waitTimeBeforeRequest: 600,
  searchPhraseMaxLength: 255,
};

@Component({
  selector: 'sd-searchbox',
  templateUrl: './custom-search-box.component.html',
  styleUrls: ['./custom-search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdCustomSearchBoxComponent implements OnInit, OnDestroy {
  iconTypes = CUSTOM_ICON_TYPE;
  /**
   * Тип подсказки
   * Используется при отправке в аналитику
   */
  suggestionTypes = SuggestionsType;
  /**
   * Конфиг с настройками поиска, полученный с бекенда
   */
  config: CustomSearchBoxConfig = DEFAULT_CONFIG;
  /**
   * Результаты поиска
   */
  results$: Observable<CustomSearchResults>;
  query$: Observable<string>;
  /**
   * Статус загрузки подсказок
   */
  isLoading$: Observable<boolean>;
  /**
   * Возвращает тру, если результатов поиска нет
   */
  isEmpty$: Observable<boolean>;
  /**
   *
   */
  searchInputControl = new FormControl();

  /**
   * Получить форматированное значение поля ввода поисковой фразы
   */
  get searchInputValue(): string {
    return this.formatSearchPhrase(this.searchInputControl.value || '');
  }

  /**
   * Установить значение поля ввода поисковой фразы
   */
  set searchInputValue(value: string) {
    this.searchInputControl.patchValue(value);
  }

  /**
   * Состояние нажатия подсказки tap
   */
  private isTapSearch = false;

  private anyQueryAnalyticsData: { searchTerm?: string; correction?: string } = {};

  private subscription: Subscription = new Subscription();

  constructor(
    private customSearchBoxService: CustomSearchBoxService,
    public router: Router,
    public searchHistoryService: SearchHistoryService,
    private searchAnalyticsService: SearchAnalyticsService,
    private urlNavigatorService: UrlNavigatorService,
    private anyQueryAnalyticsService: AnyQueryAnalyticsService,
    private searchCurrentQueryService: SearchCurrentQueryService,
    private deviceService: DeviceService,
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.customSearchBoxService.getSuggestionLoading();
    this.results$ = this.customSearchBoxService.getResults();
    this.query$ = this.searchCurrentQueryService.query$;

    this.isEmpty$ = combineLatest({ isDesktop: this.deviceService.isDesktopLgUp$, results: this.results$ }).pipe(
      map(
        ({ isDesktop, results }) =>
          (isDesktop || !results.taps?.length) &&
          !results.sts?.length &&
          !results.categories?.length &&
          !results.searchHistory?.length,
      ),
    );

    // Сброс состояния нажатия подказки, когда стерли input
    this.subscription.add(
      this.searchInputControl.valueChanges.subscribe((value: string) => {
        if (value.length === 0) {
          this.isTapSearch = false;
        }
      }),
    );

    /**
     * Отправка поискового запроса при вводе текста
     */
    this.subscription.add(
      this.searchInputControl.valueChanges
        .pipe(
          tap((value) => this.searchCurrentQueryService.setCurrentQuery(value)),
          debounceTime(this.config.waitTimeBeforeRequest),
        )
        .subscribe(() => {
          this.loadSuggestions();
        }),
    );

    /**
     * Сброс результатов поиска при уходе со страницы поиска
     */
    this.subscription.add(
      this.router.events
        .pipe(
          filter(
            (event: NavigationStart) =>
              event instanceof NavigationStart &&
              !event.url.match(/freeTextSearch=/) &&
              // если пользователя редиректнуло в результате поиска,
              // то поисковую фразу в серчбоксе не затираем
              !event.url.match(/searchRedirect=true/),
          ),
          withLatestFrom(this.isEmpty$),
          filter(([, isEmpty]) => !isEmpty),
        )
        .subscribe(() => {
          if (this.searchInputValue !== '') {
            this.searchInputValue = '';
            this.customSearchBoxService.clearResults();
          }
        }),
    );

    /**
     * Синхронизация инпута с поисковой строкой
     */
    this.subscription.add(
      this.customSearchBoxService
        .getCurrentQuery()
        .pipe(filter((query) => query !== this.searchInputValue))
        .subscribe((query): void => {
          this.searchInputValue = query;
        }),
    );

    this.subscription.add(
      this.customSearchBoxService.getSuggestionResults().subscribe((results) => {
        if (results?.query) {
          const searchTerm = results?.query;
          const correction = results?.correction ?? searchTerm;
          this.anyQueryAnalyticsData = { searchTerm, correction };
          this.anyQueryAnalyticsService.onLoadData({ searchTerm, correction });
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onFocusSearchInput(): void {
    this.loadSuggestions();
  }

  onEnterSearchInput(): void {
    if (!this.searchInputValue || this.searchInputValue.length < this.config.minCharactersBeforeRequest) {
      return;
    }

    this.results$
      .pipe(
        /**
         * Если очень быстро ввести поисковую фразу,
         * а затем нажать enter, то тут мы получим старые
         * результаты поиска, поэтому необходимо дождаться
         * результаты поиска с последней введенной фразой
         */
        filter((r) => r.freeTextSearch === this.searchInputValue),
        take(1),
      )
      .subscribe((results: CustomSearchResults) => {
        const freeTextSearch = results.freeTextSearch;
        const redirectUrl = results.redirect;

        if (this.isTapSearch) {
          this.sendToAnalytics(SuggestionsType.Tap, '');
          this.searchProducts(freeTextSearch, { redirectUrl, suggestionType: SuggestionsType.Tap });
        } else {
          this.searchAnalyticsService.onLaunchSearchResultWithoutSuggestion();
          this.searchProducts(freeTextSearch, { redirectUrl });
        }
      });
  }

  onClickTapItem(item: Tap): void {
    this.isTapSearch = true;
    this.searchInputValue = item.relatedSearch;
  }

  onClickStsItem(item: Sts): void {
    this.sendToAnalytics(SuggestionsType.Sts, item.st);
    this.searchInputValue = item.st;
    this.searchProducts(item.st, { suggestionType: SuggestionsType.Sts, redirectUrl: item.redirectUrl });
  }

  onClickHistoryItem(item: SearchHistoryItem): void {
    this.sendToAnalytics(SuggestionsType.History, item.text);
    this.searchInputValue = item.text;
    this.searchProducts(item.text, {
      suggestionType: SuggestionsType.History,
      redirectUrl: item.redirectUrl,
    });
  }

  onClickCategoryItem(item: { name: string; code: string }): void {
    this.sendToAnalytics(SuggestionsType.Category, item.name);
    this.searchCategory(item.name, item.code);
  }

  onClickBrandItem(item: { name: string; url: string }): void {
    this.sendToAnalytics(SuggestionsType.Brand, item.name);
    this.searchBrand(item.name);
  }

  /**
   * Очистить текстовое поле поиска
   */
  clear(): void {
    this.searchInputControl.patchValue('');
    this.loadSuggestions();
  }

  private sendToAnalytics(type: SuggestionsType, value: string): void {
    this.searchAnalyticsService.onClickSuggestion({
      searchPhrase: this.searchInputValue || 'null',
      suggestionType: type,
      suggestionValue: value,
    });
    const { searchTerm, correction } = this.anyQueryAnalyticsData;
    const item = value;
    if (typeof searchTerm === 'string') {
      switch (type) {
        case SuggestionsType.Sts:
          this.anyQueryAnalyticsService.onClickSts({ searchTerm, correction, item });
          break;
        case SuggestionsType.History:
          this.anyQueryAnalyticsService.onClickHistory({ searchTerm, correction, item });
          break;
        case SuggestionsType.Category:
          this.anyQueryAnalyticsService.onClickCategory({ searchTerm, correction, item });
          break;
        default:
          break;
      }
    }
  }

  /**
   * Поиск по продуктам
   */
  private searchProducts(
    text: string,
    {
      redirectUrl,
      suggestionType,
    }: {
      redirectUrl?: string;
      suggestionType?: SuggestionsType;
    } = {},
  ): void {
    this.searchHistoryService.push({ text, redirectUrl });
    this.customSearchBoxService.searchLaunch(text);

    if (redirectUrl) {
      this.urlNavigatorService.navigate(redirectUrl, {
        queryParams: { suggestionType, searchRedirect: true },
      });
    } else {
      this.customSearchBoxService.launchSearchPage({ suggestionType, freeTextSearch: text });
    }
  }

  /**
   * Поиск по товарам с зажатым фасетом по категории
   */
  private searchCategory(name: string, code: string): void {
    this.searchHistoryService.push({ text: this.searchInputValue });
    this.customSearchBoxService.searchLaunch(this.searchInputValue);

    const facets = SearchFacetService.createSearchQuery({
      activeFacets: [
        {
          contentType: CustomFacetContentType.Checkbox,
          name: 'Категория',
          code: 'categoryFacetPromo',
          value: { name, code },
        },
      ],
    });

    this.customSearchBoxService.launchSearchPage({
      facets,
      suggestionType: SuggestionsType.Category,
      freeTextSearch: this.searchInputValue,
    });
  }

  /**
   * Поиск по товарам с зажатым фасетом по бренду
   */
  private searchBrand(name: string): void {
    this.searchHistoryService.push({ text: this.searchInputValue });
    this.customSearchBoxService.searchLaunch(this.searchInputValue);

    const facets = SearchFacetService.createSearchQuery({
      activeFacets: [
        {
          contentType: CustomFacetContentType.Checkbox,
          name: 'Бренд',
          code: 'facet-brand',
          value: { name, code: name },
        },
      ],
    });

    this.customSearchBoxService.launchSearchPage({
      facets,
      suggestionType: SuggestionsType.Brand,
      freeTextSearch: this.searchInputValue,
    });
  }

  /**
   * Нормализация поисковой фразы
   */
  private formatSearchPhrase(value: string): string {
    return value.trim();
  }

  private loadSuggestions(): void {
    this.customSearchBoxService
      .getSuggestionResults()
      .pipe(take(1))
      .subscribe((result: CustomSearchResults) => {
        if (result?.freeTextSearch !== this.searchInputValue) {
          this.customSearchBoxService.search(this.searchInputValue, this.config);
        }
      });
  }
}
